import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { apiClient, type Transaction, type MetaConfig } from '../utils/api';
import { useTelegram } from '../hooks/useTelegram';

const transactionSchema = z.object({
  name: z.string().min(1, 'Название обязательно').max(200),
  money: z.number({ required_error: 'Сумма обязательна' }),
  fee: z.number().default(0),
  currency: z.string().min(1, 'Валюта обязательна'),
  account: z.string().min(1, 'Счет обязателен'),
  executor: z.string().min(1, 'Исполнитель обязателен'),
  transaction_time: z.string().min(1, 'Время транзакции обязательно'),
  destination_time: z.string().optional(),
  category: z.string().min(1, 'Категория обязательна'),
  payment: z.string().default(''),
  note: z.string().optional(),
});

type TransactionFormData = z.infer<typeof transactionSchema>;

interface Props {
  transaction?: Transaction;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function TransactionForm({ transaction, onSuccess, onCancel }: Props) {
  const [meta, setMeta] = useState<MetaConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCustomName, setShowCustomName] = useState(false);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [showCustomAccount, setShowCustomAccount] = useState(false);
  const [showCustomExecutor, setShowCustomExecutor] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { tg } = useTelegram();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<TransactionFormData>({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      name: '',
      money: 0,
      fee: 0,
      currency: '',
      account: '',
      executor: '',
      transaction_time: dayjs().format('YYYY-MM-DDTHH:mm'),
      destination_time: undefined,
      category: '',
      payment: '',
      note: undefined,
      ...transaction,
    },
  });

  useEffect(() => {
    loadMeta();
    if (transaction) {
      // Правильно форматируем дату для input[type="datetime-local"]
      const formattedTransaction = {
        ...transaction,
        transaction_time: transaction.transaction_time ? 
          dayjs(transaction.transaction_time).format('YYYY-MM-DDTHH:mm') : 
          dayjs().format('YYYY-MM-DDTHH:mm'),
        money: transaction.money || 0,
        destination_time: transaction.destination_time || undefined,
        note: transaction.note || undefined
      };
      reset(formattedTransaction);
    }
  }, [transaction, reset]);

  useEffect(() => {
    if (tg) {
      tg.MainButton.setText(transaction ? 'Сохранить' : 'Создать');
      tg.MainButton.onClick(handleMainButtonClick);
      tg.MainButton.show();

      return () => {
        tg.MainButton.offClick(handleMainButtonClick);
        tg.MainButton.hide();
      };
    }
  }, [tg, transaction]);

  const loadMeta = async () => {
    try {
      const data = await apiClient.getMeta();
      setMeta(data);
    } catch (error) {
      console.error('Failed to load meta:', error);
    }
  };

  const handleMainButtonClick = () => {
    handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: TransactionFormData) => {
    setIsLoading(true);
    try {
      const formData = {
        ...data,
        destination_time: data.transaction_time, // всегда равно transaction_time
      };

      if (transaction?.id) {
        await apiClient.updateTransaction(transaction.id, formData);
      } else {
        await apiClient.createTransaction(formData);
      }

      // Показываем уведомление об успехе
      const message = transaction ? 'Транзакция успешно обновлена!' : 'Транзакция успешно создана!';
      setSuccessMessage(message);
      setShowSuccessToast(true);
      
      // Скрываем toast через 3 секунды
      setTimeout(() => {
        setShowSuccessToast(false);
      }, 3000);

      reset();
      onSuccess?.();
    } catch (error) {
      console.error('Error saving transaction:', error);
      alert(`Ошибка: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    } finally {
      setIsLoading(false);
    }
  };


  if (!meta) {
    return <div className="p-4 text-center">Загрузка...</div>;
  }

  return (
    <div className="relative">
      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse">
          <div className="flex items-center">
            <span className="mr-2">✅</span>
            {successMessage}
          </div>
        </div>
      )}
      
      <div className="bg-telegram-secondary-bg rounded-lg p-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Название
          </label>
          {!showCustomName ? (
            <div className="space-y-2">
              <select
                className="telegram-input"
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setShowCustomName(true);
                  } else {
                    setValue('name', e.target.value);
                  }
                }}
                value={watch('name') || ''}
              >
                <option value="">Выберите из списка</option>
                {meta.names.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
                <option value="__custom__">Ввести свое</option>
              </select>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                {...register('name')}
                className="telegram-input"
                placeholder="Введите название"
              />
              <button
                type="button"
                onClick={() => setShowCustomName(false)}
                className="text-sm text-telegram-link"
              >
                Выбрать из списка
              </button>
            </div>
          )}
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Сумма (+ для дохода, - для расхода)
          </label>
          <input
            {...register('money', { valueAsNumber: true })}
            type="number"
            step="0.01"
            className="telegram-input"
            placeholder="1000.00 или -500.00"
          />
          {errors.money && <p className="text-red-500 text-sm mt-1">{errors.money.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Комиссия
          </label>
          <input
            {...register('fee', { valueAsNumber: true })}
            type="number"
            step="0.01"
            className="telegram-input"
            placeholder="0.00"
          />
          <p className="text-xs text-telegram-hint mt-1">
            Комиссия не включается в сумму money
          </p>
          {errors.fee && <p className="text-red-500 text-sm mt-1">{errors.fee.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Валюта
          </label>
          <select {...register('currency')} className="telegram-input">
            <option value="">Выберите валюту</option>
            {meta.currencies.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
          {errors.currency && <p className="text-red-500 text-sm mt-1">{errors.currency.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Счет
          </label>
          {!showCustomAccount ? (
            <div className="space-y-2">
              <select
                className="telegram-input"
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setShowCustomAccount(true);
                  } else {
                    setValue('account', e.target.value);
                  }
                }}
                value={watch('account') || ''}
              >
                <option value="">Выберите из списка</option>
                {meta.accounts.map((account) => (
                  <option key={account} value={account}>
                    {account}
                  </option>
                ))}
                <option value="__custom__">Ввести свой</option>
              </select>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                {...register('account')}
                className="telegram-input"
                placeholder="Введите счет"
              />
              <button
                type="button"
                onClick={() => setShowCustomAccount(false)}
                className="text-sm text-telegram-link"
              >
                Выбрать из списка
              </button>
            </div>
          )}
          {errors.account && <p className="text-red-500 text-sm mt-1">{errors.account.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Исполнитель
          </label>
          {!showCustomExecutor ? (
            <div className="space-y-2">
              <select
                className="telegram-input"
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setShowCustomExecutor(true);
                  } else {
                    setValue('executor', e.target.value);
                  }
                }}
                value={watch('executor') || ''}
              >
                <option value="">Выберите из списка</option>
                {meta.executors.map((executor) => (
                  <option key={executor} value={executor}>
                    {executor}
                  </option>
                ))}
                <option value="__custom__">Ввести своего</option>
              </select>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                {...register('executor')}
                className="telegram-input"
                placeholder="Введите исполнителя"
              />
              <button
                type="button"
                onClick={() => setShowCustomExecutor(false)}
                className="text-sm text-telegram-link"
              >
                Выбрать из списка
              </button>
            </div>
          )}
          {errors.executor && <p className="text-red-500 text-sm mt-1">{errors.executor.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Время транзакции
          </label>
          <input
            {...register('transaction_time')}
            type="datetime-local"
            className="telegram-input"
          />
          {errors.transaction_time && (
            <p className="text-red-500 text-sm mt-1">{errors.transaction_time.message}</p>
          )}
        </div>


        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Категория
          </label>
          {!showCustomCategory ? (
            <div className="space-y-2">
              <select
                className="telegram-input"
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setShowCustomCategory(true);
                  } else {
                    setValue('category', e.target.value);
                  }
                }}
                value={watch('category') || ''}
              >
                <option value="">Выберите из списка</option>
                {meta.categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
                <option value="__custom__">Ввести свою</option>
              </select>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                {...register('category')}
                className="telegram-input"
                placeholder="Введите категорию"
              />
              <button
                type="button"
                onClick={() => setShowCustomCategory(false)}
                className="text-sm text-telegram-link"
              >
                Выбрать из списка
              </button>
            </div>
          )}
          {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>}
        </div>


        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Примечание
          </label>
          <textarea
            {...register('note')}
            className="telegram-input"
            rows={3}
            placeholder="Дополнительная информация"
          />
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={isLoading}
            className="telegram-button flex-1"
          >
            {isLoading ? 'Сохранение...' : transaction ? 'Сохранить' : 'Создать'}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg"
            >
              Отмена
            </button>
          )}
        </div>
      </form>
      </div>
    </div>
  );
}